import { useState, useEffect, type FormEvent } from "react";
import { Card } from "@tremor/react";
import { FileText, Loader2, Download, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  useAudits, useAudit, useTenants, useGenerateReport,
  reportDownloadUrl, type Report,
} from "@/lib/queries";
import { AuditMarkdown } from "@/components/AuditMarkdown";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

// Tucked at the top of the audit detail card. Click → modal with
// auto-prefilled title/customer/period; submit → POST to backend, wait
// (1-3 min, the modal stays open with a spinner), success → modal flips
// to a "report ready" state with a Download PDF link. The new row also
// shows up in /reports automatically (mutation invalidates that query).
function GenerateReportButton({ auditId }: { auditId: number }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const tenantsQ = useTenants(open);
  const myTenant = tenantsQ.data?.items?.find((t) => t.id === user?.tenant_id);

  const generate = useGenerateReport();
  const [title, setTitle] = useState("");
  const [customer, setCustomer] = useState("");
  const [period, setPeriod] = useState("");

  // Prefill customer from the tenant display name when the dialog opens.
  // Title stays blank — operator usually wants a specific phrasing tied
  // to whatever they just read in the audit body.
  useEffect(() => {
    if (open && myTenant && !customer) {
      setCustomer(myTenant.display_name);
    }
  }, [open, myTenant, customer]);

  const reset = () => {
    generate.reset();
    setTitle("");
    setCustomer(myTenant?.display_name ?? "");
    setPeriod("");
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !customer.trim()) return;
    try {
      await generate.mutateAsync({
        auditId,
        body: {
          title:    title.trim(),
          customer: customer.trim(),
          period:   period.trim() || undefined,
        },
      });
      // Stay open — let the user see the success + download link.
    } catch {
      /* error rendered inline in the modal */
    }
  };

  const reportRow: Report | undefined = generate.data;
  let errorText: string | null = null;
  if (generate.isError) {
    errorText = generate.error instanceof ApiError
      ? `${generate.error.status} — ${generate.error.message}`
      : "Couldn't generate report. Try again.";
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
      >
        <FileText className="h-3.5 w-3.5" />
        Generate report
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => { if (!generate.isPending) { reset(); setOpen(false); } }}
        >
          <div
            className="w-[480px] max-w-[92vw] rounded-md border border-border bg-card p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold">
                Generate customer report — Audit #{auditId}
              </h2>
              <button
                type="button"
                disabled={generate.isPending}
                onClick={() => { reset(); setOpen(false); }}
                className="rounded-md p-1 text-muted-foreground hover:bg-accent disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {!reportRow && (
              <form onSubmit={onSubmit} className="space-y-3">
                <label className="block">
                  <span className="block text-xs font-medium text-muted-foreground">Title</span>
                  <input
                    type="text"
                    autoFocus
                    required
                    placeholder="e.g. Two C2 beacons detected on school LAN"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={generate.isPending}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                  />
                  <span className="mt-1 block text-[11px] text-muted-foreground">
                    Sits inside the "NWS® Security Operations Center Report" eyebrow on the cover.
                    No leading "Report on".
                  </span>
                </label>

                <label className="block">
                  <span className="block text-xs font-medium text-muted-foreground">Customer</span>
                  <input
                    type="text"
                    required
                    value={customer}
                    onChange={(e) => setCustomer(e.target.value)}
                    disabled={generate.isPending}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                  />
                  <span className="mt-1 block text-[11px] text-muted-foreground">
                    Defaults to your tenant's display name. Edit when producing a report
                    that names a different customer (e.g. NRG running an audit about Cambridge).
                  </span>
                </label>

                <label className="block">
                  <span className="block text-xs font-medium text-muted-foreground">Period (optional)</span>
                  <input
                    type="text"
                    placeholder="e.g. 24h ending 8 May 2026"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    disabled={generate.isPending}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                  />
                </label>

                {errorText && (
                  <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">
                    {errorText}
                  </div>
                )}

                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-400">
                  Reportify takes 1–3 min. The dialog stays open while it runs;
                  closing the page will not cancel it (server-side timeout: 30 min).
                </div>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    disabled={generate.isPending}
                    onClick={() => { reset(); setOpen(false); }}
                    className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={generate.isPending || !title.trim() || !customer.trim()}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {generate.isPending
                      ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</>
                      : <><FileText className="h-3.5 w-3.5" /> Generate</>}
                  </button>
                </div>
              </form>
            )}

            {reportRow && (
              <div className="space-y-3">
                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-3 text-sm text-emerald-700 dark:text-emerald-400">
                  Report generated. Also visible in the Reports tab.
                </div>
                <div className="grid grid-cols-[100px,1fr] gap-y-1 text-xs">
                  <div className="text-muted-foreground">Title</div>
                  <div className="font-medium">{reportRow.title}</div>
                  <div className="text-muted-foreground">Customer</div>
                  <div>{reportRow.customer}</div>
                  {reportRow.period && (
                    <>
                      <div className="text-muted-foreground">Period</div>
                      <div>{reportRow.period}</div>
                    </>
                  )}
                  <div className="text-muted-foreground">Size</div>
                  <div>
                    {reportRow.file_size_bytes
                      ? `${Math.round(reportRow.file_size_bytes / 1024)} KB`
                      : "—"}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => { reset(); setOpen(false); }}
                    className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent"
                  >
                    Close
                  </button>
                  <a
                    href={reportDownloadUrl(reportRow.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    <Download className="h-3.5 w-3.5" /> Download PDF
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtDuration(s: number | null): string {
  if (s === null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${r}s`;
}

export default function Audits() {
  const list = useAudits(20);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const detail = useAudit(selectedId);

  // Default to the most recent audit on first load.
  useEffect(() => {
    if (selectedId === null && list.data && list.data.items.length > 0) {
      setSelectedId(list.data.items[0].id);
    }
  }, [selectedId, list.data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Audits</h1>
        <p className="text-sm text-muted-foreground">
          Tyrael's daily processed narrative. Every run produces a ranked
          set of findings. The raw logs that fed into them live in Graylog.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px,1fr]">
        {/* History list */}
        <Card className="p-0 overflow-hidden">
          <div className="border-b border-border bg-muted/40 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            History
          </div>
          {list.isLoading && (
            <div className="px-4 py-6 text-sm text-muted-foreground">Loading…</div>
          )}
          {list.data?.items.length === 0 && (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              No audits yet. The daily 08:00 cron + the on-demand `/audit` slash command both write here.
            </div>
          )}
          <div className="max-h-[70vh] overflow-y-auto">
            {list.data?.items.map((a) => {
              const active = a.id === selectedId;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setSelectedId(a.id)}
                  className={cn(
                    "block w-full border-b border-border px-4 py-3 text-left transition-colors last:border-0",
                    active
                      ? "bg-primary/5"
                      : "hover:bg-muted/40",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold tabular-nums">#{a.id}</span>
                    {!a.ok && (
                      <span className="rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-600">
                        failed
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {fmtDateTime(a.run_at)} · {fmtDuration(a.duration_s)}
                  </div>
                  <div className="mt-2 line-clamp-3 text-xs text-foreground/80">
                    {a.teaser}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Selected audit body */}
        <Card>
          {!selectedId && (
            <div className="text-sm text-muted-foreground">
              Pick an audit on the left.
            </div>
          )}
          {selectedId && detail.isLoading && (
            <div className="text-sm text-muted-foreground">Loading audit #{selectedId}…</div>
          )}
          {detail.isError && (
            <div className="text-sm text-red-500">Failed to load this audit.</div>
          )}
          {detail.data && (
            <>
              <div className="mb-4 flex items-center justify-between gap-3 border-b border-border pb-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    Audit #{detail.data.id}
                  </div>
                  <div className="text-sm font-medium">
                    {fmtDateTime(detail.data.run_at)}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right text-xs text-muted-foreground">
                    <div>Duration: {fmtDuration(detail.data.duration_s)}</div>
                    <div>{detail.data.ok ? "Completed" : `Failed: ${detail.data.error ?? ""}`}</div>
                  </div>
                  {detail.data.ok && (
                    <GenerateReportButton auditId={detail.data.id} />
                  )}
                </div>
              </div>
              <AuditMarkdown body={detail.data.body} />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
